// src/pages/Query.js — Full-page universal search across every type in
// src/lib/searchTypes.js. Matches names, other fields and related concepts
// (taxonomy + aliases, see src/lib/searchIndex.js); every row says why it
// matched. The Dashboard quick search hands off here with ?q=. The term and the type filter live in the URL, so a query
// can be bookmarked or shared.
import { useEffect, useMemo, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate, useSearchParams } from 'react-router-dom'
import api from '../lib/api'
import { useIsPreview } from '../lib/preview'
import { resolveTypes } from '../lib/searchTypes'

const PREVIEW_ROWS = 10   // per type on the "All" view

function countLabel(g) {
  if (!g) return '0'
  return g.capped ? `${g.total}+` : String(g.total)
}

function ResultsTable({ def, group, rows, navigate, onShowAll }) {
  return (
    <section style={{ marginBottom: 28 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 10 }}>
        <h2 style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>
          {def.label} <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>({countLabel(group)})</span>
        </h2>
        {onShowAll && (
          <button type="button" className="btn btn-ghost btn-sm" onClick={onShowAll}>
            Show all {def.label.toLowerCase()} →
          </button>
        )}
      </div>
      <div className="table-wrapper">
        <table>
          <thead>
            <tr>{def.columns.map(c => <th key={c.label}>{c.label}</th>)}</tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.id} onClick={def.to ? () => navigate(def.to(r.id)) : undefined} style={{ cursor: def.to ? 'pointer' : 'default' }}>
                {def.columns.map(c => <td key={c.label}>{c.cell(r)}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {group?.results.length < group?.total && !onShowAll && (
        <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 8 }}>
          Showing the best {group.results.length} of {countLabel(group)} matches. Refine the term to narrow it down.
        </p>
      )}
    </section>
  )
}

export default function Query() {
  const isPreview = useIsPreview()
  const navigate = useNavigate()
  const inputRef = useRef(null)
  const [searchParams, setSearchParams] = useSearchParams()
  const urlTerm = searchParams.get('q') || ''
  const type    = searchParams.get('type') || 'all'
  const [text, setText] = useState(urlTerm)

  useEffect(() => { inputRef.current?.focus() }, [])
  // Keep the box in sync when the URL changes (quick search hand-off, back button).
  useEffect(() => {
    setText(t => (t.trim() === urlTerm ? t : urlTerm))
  }, [urlTerm])

  // Debounce typing into the URL; the URL drives the query.
  useEffect(() => {
    const t = setTimeout(() => {
      const next = text.trim()
      if (next === urlTerm) return
      const params = {}
      if (next) params.q = next
      if (type !== 'all') params.type = type
      setSearchParams(params, { replace: true })
    }, 250)
    return () => clearTimeout(t)
  }, [text]) // eslint-disable-line react-hooks/exhaustive-deps

  function setType(next) {
    const params = {}
    if (urlTerm) params.q = urlTerm
    if (next !== 'all') params.type = next
    setSearchParams(params)
  }

  const term = urlTerm.trim()
  const { data, isFetching, error } = useQuery({
    queryKey: ['query-page', term],
    queryFn: () => api.get('/api/dashboard/search', { params: { q: term, limit: 200 } }).then(r => r.data.data),
    enabled: term.length >= 2,
    staleTime: 30 * 1000,
    placeholderData: prev => prev,
  })

  const defs = useMemo(() => resolveTypes(data, navigate, term), [data, navigate, term])
  const totalAll = data ? defs.reduce((n, d) => n + (data[d.key]?.total ?? 0), 0) : 0
  const anyCapped = data ? defs.some(d => data[d.key]?.capped) : false
  const visible = type === 'all' ? defs : defs.filter(d => d.key === type)
  const hasResults = data && visible.some(d => data[d.key]?.results.length)

  return (
    <div className="page">
      <div className="page-header">
        <div className="page-header-text">
          <h1>Query</h1>
          {!isPreview && <p>Search {defs.map(d => d.label.toLowerCase()).join(', ')} by name, field or concept (e.g. private equity). Each result says why it matched.</p>}
        </div>
      </div>

      <div style={{ position: 'relative', maxWidth: 640, marginBottom: 16 }}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
          strokeLinecap="round" strokeLinejoin="round"
          style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)', pointerEvents: 'none' }}>
          <circle cx="11" cy="11" r="7" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
        </svg>
        <input
          ref={inputRef}
          className="input"
          type="search"
          placeholder="Type at least 2 characters"
          aria-label="Query"
          value={text}
          onChange={e => setText(e.target.value)}
          style={{ width: '100%', paddingLeft: 36, fontSize: 14, height: 40 }}
        />
      </div>

      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 24 }}>
        {[{ key: 'all', label: 'All' }, ...defs].map(d => {
          const count = d.key === 'all' ? `${totalAll}${anyCapped ? '+' : ''}` : countLabel(data?.[d.key])
          return (
            <button key={d.key} type="button" onClick={() => setType(d.key)} aria-pressed={type === d.key}
              className={`btn btn-sm ${type === d.key ? 'btn-primary' : 'btn-secondary'}`}>
              {d.label}{term.length >= 2 && data ? ` (${count})` : ''}
            </button>
          )
        })}
        {isFetching && <div className="spinner" style={{ marginLeft: 8, alignSelf: 'center' }} />}
      </div>

      {term.length >= 2 && data?.concepts?.length > 0 && (
        <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: -12, marginBottom: 20 }}>
          Includes taxonomy terms (and everything under them): {data.concepts.map(c => c.via === 'definition' ? `${c.label} (by definition)` : c.label).join(', ')}
        </p>
      )}

      {term.length < 2 ? (
        <div className="empty-state" style={{ paddingTop: 40 }}>
          <h3>Start typing to search</h3>
          <p>Try a name, a firm, or a concept like private equity, family office or secondaries.</p>
        </div>
      ) : error ? (
        <div className="empty-state" style={{ paddingTop: 40 }}>
          <h3>Search failed</h3>
          <p>{error.response?.data?.error || error.message}</p>
        </div>
      ) : data && !hasResults ? (
        <div className="empty-state" style={{ paddingTop: 40 }}>
          <h3>No matches for "{term}"</h3>
          <p>Try a shorter term or a related concept.</p>
        </div>
      ) : data && visible.map(def => {
        const group = data[def.key]
        if (!group?.results.length) return null
        const preview = type === 'all' && group.results.length > PREVIEW_ROWS
        return (
          <ResultsTable
            key={def.key}
            def={def}
            group={group}
            rows={preview ? group.results.slice(0, PREVIEW_ROWS) : group.results}
            navigate={navigate}
            onShowAll={preview || (type === 'all' && group.total > group.results.length) ? () => setType(def.key) : null}
          />
        )
      })}
    </div>
  )
}
